import axios from 'axios';
import { API_CONFIG } from '../config/api';

export interface SearchFilters {
    country_code?: string;
    legal_form?: string;
    lou?: string;
}

export interface SearchParams {
    query: string;
    filters?: SearchFilters;
    page?: number;
    size?: number;
    searchType?: 'exact' | 'fuzzy';
    searchFields?: string[];
}

export interface LEIRecord {
    lei: string;
    registered_name: string;
    registered_country_code?: string;
    legal_form?: string;
    // Registered address fields (for backward compatibility)
    registered_address_line1?: string;
    registered_address_line2?: string;
    registered_address_line3?: string;
    registered_address_line4?: string;
    registered_city?: string;
    registered_region?: string;
    registered_postal_code?: string;
    // Legal address fields (what we call "Business Address" in UI)
    legal_address_line_1?: string;
    legal_address_line_2?: string;
    legal_address_line_3?: string;
    legal_address_line_4?: string;
    legal_city?: string;
    legal_country_code?: string;
    legal_postal_code?: string;
    legal_region?: string;
    // Headquarters address fields
    headquarters_address_line_1?: string;
    headquarters_address_line_2?: string;
    headquarters_address_line_3?: string;
    headquarters_address_line_4?: string;
    headquarters_city?: string;
    headquarters_country_code?: string;
    headquarters_postal_code?: string;
    headquarters_region?: string;
    // Registry and LEI information
    business_registry_identifier?: string;
    business_registry_name?: string;
    business_registry_code?: string;
    registered_by?: string;
    lou?: string;
    lei_assignment_date?: string;
    entity_status_code?: string;
    lei_status_code?: string;
    lei_record_last_update?: string;
    lei_next_renewal_date?: string;
    legal_jurisdiction?: string;
    inferred_jurisdiction?: string;
    other_names?: string[];
    transliterated_name?: string;
    retrieved_at?: string;
}

export interface SearchResponse {
    results: LEIRecord[];
    total: number;
    page: number;
    size: number;
    facets: {
        country_codes: Array<{ key: string; count: number }>;
        legal_forms: Array<{ key: string; count: number }>;
        lous: Array<{ key: string; count: number }>;
    };
}

export interface FacetResponse {
    country_codes: Array<{ key: string; count: number }>;
    legal_forms: Array<{ key: string; count: number }>;
    lous: Array<{ key: string; count: number }>;
}

class SearchService {
    private api = axios.create({
        baseURL: API_CONFIG.baseURL,
        timeout: API_CONFIG.timeout
    });

    /**
     * Search for LEI records
     */
    async search(params: SearchParams): Promise<SearchResponse> {
        try {
            // Process query for OpenSearch
            let processedQuery = params.query.trim();

            // Only modify query for exact search - let OpenSearch handle fuzzy search naturally
            if (params.searchType === 'exact') {
                if (!processedQuery.startsWith('"') || !processedQuery.endsWith('"')) {
                    processedQuery = `"${processedQuery}"`;
                }
            }
            // For fuzzy search, send query as-is and let OpenSearch handle it

            const response = await this.api.post('/api/v1/search', {
                query: processedQuery,
                search_type: params.searchType || 'fuzzy',
                search_fields: params.searchFields || [],
                country_code: params.filters?.country_code,
                legal_form: params.filters?.legal_form,
                lou: params.filters?.lou,
                page: params.page || 1,
                size: params.size || 20
            });
            return response.data;
        } catch (error) {
            console.error('Search error:', error);
            throw new Error('Failed to search LEI records');
        }
    }

    /**
     * Get search facets for filtering
     */
    async getFacets(): Promise<FacetResponse> {
        try {
            const response = await this.api.get('/api/v1/facets');
            return response.data;
        } catch (error) {
            console.error('Facets error:', error);
            throw new Error('Failed to get search facets');
        }
    }

    /**
     * Get a specific LEI record by ID
     */
    async getRecord(lei: string): Promise<LEIRecord> {
        try {
            const response = await this.api.get(`/api/v1/record/${lei}`);
            return response.data;
        } catch (error) {
            console.error('Record fetch error:', error);
            throw new Error('Failed to fetch LEI record');
        }
    }

    /**
     * Export search results
     */
    async exportResults(params: SearchParams, format: 'csv' | 'json'): Promise<Blob> {
        try {
            const exportParams = {
                query: params.query,
                search_type: params.searchType || 'fuzzy',
                search_fields: params.searchFields?.join(',') || '',
                ...(params.filters?.country_code && {
                    country_code: params.filters.country_code
                }),
                ...(params.filters?.legal_form && { legal_form: params.filters.legal_form }),
                ...(params.filters?.lou && { lou: params.filters.lou }),
                export_format: format
            };

            console.log('Export URL params:', exportParams);

            const response = await this.api.get('/api/v1/export', {
                params: exportParams,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('Export error:', error);
            console.error('Export params:', {
                query: params.query,
                searchType: params.searchType,
                searchFields: params.searchFields,
                filters: params.filters,
                format
            });
            if (error instanceof Error && 'response' in error) {
                const axiosError = error as Error & {
                    response?: {
                        data?: unknown;
                        status?: number;
                    };
                };
                console.error('Response status:', axiosError.response?.status);

                // Try to read the blob response as text to get the actual error message
                if (axiosError.response?.data instanceof Blob) {
                    try {
                        const errorText = await (axiosError.response.data as Blob).text();
                        console.error('Error response text:', errorText);
                        throw new Error(`Export failed: ${errorText}`);
                    } catch (blobError) {
                        console.error('Could not read error blob:', blobError);
                    }
                } else {
                    console.error('Response data:', axiosError.response?.data);
                }
            }
            throw new Error('Failed to export search results');
        }
    }

    /**
     * Get search statistics
     */
    async getStats(): Promise<{ totalRecords: number; lastUpdate: string }> {
        try {
            const response = await this.api.get('/api/v1/stats');
            return response.data;
        } catch (error) {
            console.error('Stats error:', error);
            throw new Error('Failed to get search statistics');
        }
    }
}

export const searchService = new SearchService();
export default searchService;
